<template>
    <div class="envelope-signers">
        <header class="envelope-signers-heading">
            <h2 class="text-base font-semibold">Signers</h2>
            <span class="text-xs text-gray-500 font-normal">{{ signerCount }} {{ signerCount === 1 ? 'signer' : 'signers' }}</span>
        </header>
        <div v-if="editable" class="envelope-signers-toolbar">
            <span class="signer-save-status text-xs text-gray-500" role="status">{{ toast }}</span>
            <button type="button" class="envelope-button" @click="openSigner()"><user-icon size="16" aria-hidden="true" />Add a signer</button>
        </div>
        <div class="envelope-signers-content" @scroll="menu.show = false">
            <div v-if="property.broker && (editable || broker_will_sign_proxy)" class="signer-row">
                <div class="flex-1 min-w-0" :class="{ 'opacity-50': !broker_will_sign_proxy }">
                    <div class="flex items-center gap-2 text-base font-semibold"><user-icon size="16" class="flex-none text-gray-500" aria-hidden="true" /><span class="truncate">{{ property.broker.name }} <span class="text-xs font-normal text-gray-500">(Broker)</span></span></div>
                    <div class="text-xs text-gray-500 truncate pl-6">{{ property.broker.email }}</div>
                </div>
                <label v-if="editable" class="flex items-center gap-2 text-xs text-gray-500 flex-none"><input type="checkbox" v-model="broker_will_sign_proxy" @change="$emit('brokerWillSignUpdated', broker_will_sign_proxy)" />Broker will sign</label>
                <span v-else-if="envelope.broker_completed_at" class="text-xs text-blue-600 text-right">Signed<br>{{ signedAt(envelope.broker_completed_at) }}</span>
            </div>
            <draggable v-model="recipients_proxy" :disabled="!editable" :animation="150" filter="button" :prevent-on-filter="false" @start="menu.show = false" @end="emitRecipients">
                <div v-for="recipient in recipients_proxy" :key="recipient.id" class="signer-row">
                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2 text-base font-semibold"><user-icon size="16" class="flex-none text-gray-500" aria-hidden="true" /><span class="truncate">{{ recipient.name }}</span></div>
                        <div class="text-xs text-gray-500 truncate pl-6">{{ recipient.email }}</div>
                    </div>
                    <span v-if="recipient.completed_at" class="text-xs text-blue-600 text-right">Signed<br>{{ signedAt(recipient.completed_at) }}</span>
                    <button v-if="editable" type="button" class="signer-menu" :aria-label="`Actions for ${recipient.name}`" aria-haspopup="menu" @click.stop="openMenu($event, recipient)"><more-horizontal-icon size="16" aria-hidden="true" /></button>
                </div>
            </draggable>
            <p v-if="!signerCount" class="text-xs text-gray-500 text-center py-6">No signers added.</p>
        </div>
        <footer class="envelope-signers-footer text-xs text-gray-500">Specify one or more people who must sign the documents in this envelope.</footer>
        <contextual-dropdown :show="menu.show" :items="menuItems" :x="menu.x" :y="menu.y" @item-clicked="menuAction" @close="menu.show = false" />
        <modal :name="modalName" :adaptive="true" :focusTrap="true" :width="480" height="auto" @opened="$refs.signerName.focus()">
            <form class="envelope-edit-modal p-6 bg-white" @submit.prevent="saveSigner">
                <h2 class="text-lg font-semibold pb-4 mb-4">{{ editingId === null ? 'Add a signer' : 'Edit signer' }}</h2>
                <label class="block text-sm mb-4">Name<input ref="signerName" v-model="draft.name" class="signer-input" required maxlength="255" /></label>
                <label class="block text-sm mb-4">Email<input v-model="draft.email" class="signer-input" type="email" required maxlength="255" /></label>
                <p v-if="error" class="text-sm text-red-700" role="alert">{{ error }}</p>
                <div class="flex justify-end gap-3 mt-6"><button type="button" class="envelope-button" @click="$modal.hide(modalName)">Cancel</button><button type="submit" class="envelope-button envelope-primary" :disabled="!draft.name.trim() || !draft.email.trim()">Save</button></div>
            </form>
        </modal>
    </div>
</template>
<script>
import { UserIcon, MoreHorizontalIcon } from 'vue-feather-icons';
import draggable from 'vuedraggable';
import ContextualDropdown from '../../../components/contextual-dropdown.vue';

export default {
    components: { UserIcon, MoreHorizontalIcon, draggable, ContextualDropdown },
    props: { read_only: { default: false }, property: {}, envelope: {}, broker_will_sign: {}, statuses: { default: () => [] }, toast: {} },
    data() {
        return { recipients_proxy: (this.envelope?.recipients || []).map(item => ({ ...item })), broker_will_sign_proxy: this.broker_will_sign,
            menu: { show: false, x: 0, y: 0, recipient: null }, editingId: null, draft: { name: '', email: '' }, error: '', nextId: 0 };
    },
    computed: {
        modalName() { return `envelope-signer-${this._uid}`; },
        editable() { return !this.read_only && !this.envelope?.sent_at; },
        signerCount() { return this.recipients_proxy.length + (this.broker_will_sign_proxy && this.property.broker ? 1 : 0); },
        menuItems() {
            const index = this.recipients_proxy.findIndex(item => item.id === this.menu.recipient?.id);
            return [{ key: 'edit', label: 'Edit', icon: 'fas fa-edit' },
                ...(index > 0 ? [{ key: 'up', label: 'Move up', icon: 'fas fa-arrow-up' }] : []),
                ...(index < this.recipients_proxy.length - 1 ? [{ key: 'down', label: 'Move down', icon: 'fas fa-arrow-down' }] : []),
                { key: 'remove', label: 'Remove', icon: 'fas fa-trash-alt', color: 'red' }];
        },
    },
    methods: {
        signedAt(timestamp) { return timestamp ? dayjs(timestamp).format('M/D/YYYY [at] h:mm A') : ''; },
        openSigner(recipient = null) {
            if (!this.editable) return;
            this.editingId = recipient?.id ?? null;
            this.draft = { name: recipient?.name || '', email: recipient?.email || '' };
            this.error = '';
            this.$modal.show(this.modalName);
        },
        saveSigner() {
            if (!this.editable) return;
            const name = this.draft.name.trim();
            const email = this.draft.email.trim();
            if (!name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { this.error = 'Enter a name and valid email address.'; return; }
            if (this.recipients_proxy.some(item => item.id !== this.editingId && item.email.toLowerCase() === email.toLowerCase())) { this.error = 'This email is already in the signer list.'; return; }
            const index = this.recipients_proxy.findIndex(item => item.id === this.editingId);
            if (index >= 0) this.$set(this.recipients_proxy, index, { ...this.recipients_proxy[index], name, email });
            else this.recipients_proxy.push({ id: `new-${++this.nextId}`, name, email });
            this.emitRecipients();
            this.$modal.hide(this.modalName);
        },
        emitRecipients() { if (this.editable) this.$emit('recipientsUpdated', this.recipients_proxy.map((item, index) => ({ ...item, signing_order: index + 1 }))); },
        openMenu(event, recipient) { const rect = event.currentTarget.getBoundingClientRect(); this.menu = { show: true, x: rect.right, y: rect.bottom, recipient }; },
        menuAction(item) {
            if (!this.editable) return;
            const recipient = this.menu.recipient;
            this.menu.show = false;
            if (item.key === 'edit') { this.openSigner(recipient); return; }
            const index = this.recipients_proxy.findIndex(row => row.id === recipient.id);
            if (index < 0) return;
            if (item.key === 'remove') this.recipients_proxy.splice(index, 1);
            else {
                const destination = index + (item.key === 'up' ? -1 : 1);
                if (destination < 0 || destination >= this.recipients_proxy.length) return;
                this.recipients_proxy.splice(destination, 0, this.recipients_proxy.splice(index, 1)[0]);
            }
            this.emitRecipients();
        },
        updateRecipientStatuses(statuses) {
            statuses.forEach(status => {
                const recipient = this.recipients_proxy.find(item => item.id === status.id);
                if (recipient) ['status', 'sent_at', 'completed_at'].forEach(key => this.$set(recipient, key, status[key]));
            });
        },
    },
    watch: { statuses: { deep: true, handler(value) { this.updateRecipientStatuses(value); } } },
    created() { this.updateRecipientStatuses(this.statuses); },
};
</script>
<style scoped>
.envelope-signers { position: relative; display: flex; flex-direction: column; height: 100%; min-height: 0; }
.envelope-signers-heading { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; min-height: 49px; flex: none; padding: 0.5rem 0.75rem; background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
.envelope-signers-heading h2 { margin: 0; }
.envelope-signers-toolbar { display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; height: 52px; padding: 0.35rem 0.75rem; border-bottom: 1px solid #e5e7eb; }
.signer-save-status { flex: 1; min-width: 0; height: 1rem; }
.envelope-signers-content { flex: 1; min-height: 0; overflow-y: auto; }
.signer-row { display: flex; align-items: center; gap: 0.625rem; padding: 0.625rem 0.75rem; border-bottom: 1px solid #f3f4f6; cursor: default; }
.signer-row:hover { background: #f9fafb; }
.signer-row.sortable-chosen { cursor: move; }
.signer-menu { display: inline-flex; align-items: center; justify-content: center; border: 1px solid #d1d5db; border-radius: 9999px; width: 1.5rem; height: 1.5rem; flex: none; color: #6b7280; }
.signer-menu:hover { color: #2563eb; border-color: #2563eb; }
.signer-input { display: block; width: 100%; margin-top: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; padding: 0.55rem 0.75rem; }
.envelope-signers-footer { flex-shrink: 0; border-top: 1px solid #e5e7eb; padding: 0.625rem 0.75rem; }
</style>
